// ═══════════════════════════════════════════════════════════════════════════
// PAGE ADMIN — GUIDES DE RÉUNION (référentiel)
// ═══════════════════════════════════════════════════════════════════════════
//
// Référentiel tenant : les six guides système livrés par Ornexia (lecture
// seule) plus ceux ajoutés par l'administrateur. L'ordre se règle au
// glisser-déposer, comme les comités et les étapes.
//
// Le CHOIX des guides applicables à un type de comité ne se fait PAS ici :
// il vit sur la fiche du comité (#admin/committees/:id), au plus près de
// l'objet qu'il configure.

import { initDragOrder } from '../utils/drag-order.js';

export interface GuideDefinition {
  id: number;
  code: string | null;
  name: string;
  description: string;
  phase: 'prepare' | 'animate' | 'conclude';
  is_system: boolean;
  position: number;
  /** Modèle d'action proposé depuis la fiche réunion. Vide = aucune action. */
  action_label: string;
  action_for_animators: boolean;
  action_for_participants: boolean;
  /** Écart signé par rapport à la date de réunion, en jours. */
  action_delay_days: number;
  /** Préfixer le libellé de l'action par « [réunion - date] ». */
  action_quote_meeting: boolean;
}

const PHASES: ReadonlyArray<GuideDefinition['phase']> = ['prepare', 'animate', 'conclude'];

let mgDefs: GuideDefinition[] = [];

function mgPhaseLabel(phase: GuideDefinition['phase']): string {
  return t('pageMeeting.guides.phase.' + phase);
}

function mgSort(rows: GuideDefinition[]): GuideDefinition[] {
  return [...rows].sort((a, b) => a.position - b.position || a.id - b.id);
}

function mgFail(err: unknown): void {
  toast(err instanceof Error ? err.message : t('common.error'), 'error');
}

// ── Rendu ────────────────────────────────────────────────────────────────────

// `meeting_guides.read` garde la route, mais c'est une clé de menu : elle
// n'autorise pas l'écriture. Sans ce test, Créer / ✎ / ✕ et le glisser-déposer
// étaient rendus à tout le monde (revue front §I3).
const canWriteGuides = (): boolean =>
  !!(window.hasPermission && window.hasPermission('meeting_guides.write'));

function mgRow(d: GuideDefinition): string {
  const canWrite = canWriteGuides();
  // `.badge-priv` (bleu informatif) plutôt qu'une classe de plus : le badge
  // signale une origine, pas un état de succès.
  const badge = d.is_system
    ? `<span class="badge badge-priv" title="${esc(t('meetingGuides.admin.systemHint'))}">${esc(
        t('meetingGuides.admin.system'),
      )}</span>`
    : '';
  // Le crayon est proposé même sur un guide Ornexia : son ACTION STANDARD
  // est modifiable, seuls nom / description / phase restent verrouillés (la
  // popup les rend en lecture seule). La corbeille, elle, reste réservée aux
  // guides du tenant.
  const actions = !canWrite
    ? ''
    : `<button class="btn btn-secondary btn-icon mg-edit" data-id="${d.id}" title="${esc(t('common.edit'))}">✎</button>` +
      (d.is_system
        ? ''
        : `<button class="btn btn-danger btn-icon mg-del" data-id="${d.id}" style="margin-left:4px" title="${esc(t('common.delete'))}">✕</button>`);
  return `<tr class="drag-row" draggable="${canWrite}" data-def-id="${d.id}">
      <td style="width:32px;text-align:center">${
        canWrite
          ? `<span class="drag-handle" role="button" aria-label="${esc(
              t('stages.dragdrop.aria_handle'),
            )}">⠿</span>`
          : ''
      }</td>
      <td><div style="display:flex;align-items:center;gap:6px">
        ${
          /* Même style que le nom d'un modèle dans #admin/checklists : cliquable
             sans être souligné, l'infobulle disant ce que fait le clic. Le
             soulignement est réservé aux vrais liens de navigation. */ ''
        }
        <span ${canWrite ? `class="mg-open" data-id="${d.id}"` : ''} style="cursor:${canWrite ? 'pointer' : 'default'};font-size:13px;font-weight:500" ${canWrite ? `title="${esc(t('common.edit'))}"` : ''}>${esc(d.name)}</span>${badge}
      </div></td>
      <td class="text-sm" style="color:var(--text2)">${esc(d.description)}</td>
      <td style="white-space:nowrap">${esc(mgPhaseLabel(d.phase))}</td>
      <td style="white-space:nowrap;text-align:right">${actions}</td>
    </tr>`;
}

/** Rendu pur du référentiel. Exporté pour être testé sans DOM. */
export function renderReferentialCard(defs: GuideDefinition[]): string {
  const rows = defs.map(mgRow).join('');
  return `
    <div class="card" id="mg-card">
      <p class="text-sm" style="color:var(--text2);margin:0 0 16px 0;line-height:1.6">${esc(
        t('meetingGuides.admin.intro'),
      )}</p>
      ${
        defs.length && canWriteGuides()
          ? `<p style="font-size:12px;color:var(--text3);margin:0 0 10px 0">${esc(
              t('meetingGuides.admin.dragHelp'),
            )}</p>`
          : ''
      }
      <div class="table-wrap"><table>
        <thead><tr>
          <th style="width:32px"></th>
          <th style="min-width:230px">${esc(t('meetingGuides.admin.name'))}</th>
          <th>${esc(t('meetingGuides.admin.description'))}</th>
          <th style="width:110px">${esc(t('meetingGuides.admin.phase'))}</th>
          <th style="width:96px"></th>
        </tr></thead>
        <tbody id="mg-tbody">${
          rows ||
          `<tr><td colspan="5" class="empty">${esc(t('meetingGuides.admin.empty'))}</td></tr>`
        }</tbody>
      </table></div>
    </div>`;
}

function mgPaint(): void {
  setContent(renderReferentialCard(mgDefs));
  mgWire();
}

// ── Données ──────────────────────────────────────────────────────────────────

async function mgReload(): Promise<void> {
  mgDefs = mgSort((await api.getGuideDefinitions()) as GuideDefinition[]);
}

// ── Câblage ──────────────────────────────────────────────────────────────────

function mgWire(): void {
  document.getElementById('btn-new-guide')?.addEventListener('click', () => mgShowModal(null));

  // Le crayon ET le libellé ouvrent l'édition — comme sur les comités, où le
  // code et le nom sont cliquables. Les guides système n'ont pas de libellé
  // cliquable : l'API refuse de les modifier (409).
  document.querySelectorAll<HTMLElement>('.mg-edit, .mg-open').forEach((b) =>
    b.addEventListener('click', () => {
      const d = mgDefs.find((x) => x.id === Number(b.dataset['id']));
      if (d) mgShowModal(d);
    }),
  );

  document.querySelectorAll<HTMLButtonElement>('.mg-del').forEach((b) =>
    b.addEventListener('click', () => {
      const d = mgDefs.find((x) => x.id === Number(b.dataset['id']));
      if (d) mgConfirmDelete(d);
    }),
  );

  const tbody = document.getElementById('mg-tbody');
  if (!tbody || !canWriteGuides()) return;
  initDragOrder({
    container: tbody,
    itemSelector: 'tr.drag-row',
    idKey: 'defId',
    onReorder: (order) => {
      void (async () => {
        try {
          mgDefs = mgSort((await api.reorderGuideDefinitions(order)) as GuideDefinition[]);
          toast(t('meetingGuides.admin.reordered'));
          mgPaint();
        } catch (err: unknown) {
          mgFail(err);
          await mgReload();
          mgPaint();
        }
      })();
    },
  });
}

// ── Modales ──────────────────────────────────────────────────────────────────

function mgConfirmDelete(d: GuideDefinition): void {
  openModal(
    t('meetingGuides.admin.deleteTitle'),
    `<p>${esc(t('meetingGuides.admin.deleteMessage').replace('{name}', d.name))}</p>`,
    async () => {
      try {
        await api.deleteGuideDefinition(d.id);
        closeModal();
        toast(t('meetingGuides.admin.deleted'));
        await mgReload();
        mgPaint();
      } catch (err: unknown) {
        mgFail(err);
      }
    },
  );
}

/**
 * Corps de la popup. Rendu pur, exporté pour être testé sans DOM.
 *
 * Sur un guide Ornexia, nom / description / phase sont en lecture seule :
 * les modifier romprait la comparabilité entre tenants, et l'API renvoie 409.
 * Le bloc « action standard », lui, reste ouvert — c'est une décision propre à
 * chaque organisation.
 */
export function mgModalBody(existing: GuideDefinition | null): string {
  const locked = existing?.is_system === true;
  // `disabled` seul ne se voit pas : la feuille de style de base ne met pas en
  // forme les champs désactivés (constaté au navigateur). On grise donc ici,
  // sinon le verrou ne se devine qu'en cliquant.
  const roStyle = locked ? ';background:var(--bg2);color:var(--text3);cursor:not-allowed' : '';
  const ro = locked ? ' disabled' : '';
  const lockHint = locked
    ? `<p class="text-sm text-muted" style="margin:0 0 10px">${esc(t('meetingGuides.admin.systemLocked'))}</p>`
    : '';
  const checkbox = (id: string, labelKey: string, checked: boolean): string =>
    `<label style="display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer;padding:4px 0">
        <input type="checkbox" id="${id}"${checked ? ' checked' : ''}>
        <span>${esc(t(labelKey))}</span>
      </label>`;
  return `
    ${lockHint}
    <div class="form-row">
      <div class="form-group" style="flex:1">
        <label for="mgm-name">${esc(t('meetingGuides.admin.name'))}</label>
        <input type="text" id="mgm-name" maxlength="120" style="width:100%${roStyle}"${ro}
          value="${esc(existing?.name ?? '')}" placeholder="${esc(t('meetingGuides.admin.namePlaceholder'))}">
      </div>
      <div class="form-group" style="flex:0 0 160px;min-width:140px">
        <label for="mgm-phase">${esc(t('meetingGuides.admin.phase'))}</label>
        <select id="mgm-phase" style="width:100%${roStyle}"${ro}>
          ${PHASES.map(
            (p) =>
              `<option value="${p}"${existing?.phase === p ? ' selected' : ''}>${esc(mgPhaseLabel(p))}</option>`,
          ).join('')}
        </select>
      </div>
    </div>
    <div class="form-row">
      <div class="form-group" style="flex:1">
        <label for="mgm-desc">${esc(t('meetingGuides.admin.description'))}</label>
        <textarea id="mgm-desc" rows="3" style="width:100%${roStyle}"${ro} placeholder="${esc(
          t('meetingGuides.admin.descriptionPlaceholder'),
        )}">${esc(existing?.description ?? '')}</textarea>
      </div>
    </div>
    <p style="font-size:11px;color:var(--text3);margin:4px 0 0">${esc(
      t('meetingGuides.admin.modalHelp'),
    )}</p>

    <div class="card" style="margin-top:14px;background:var(--bg2)">
      <div class="card-header"><h3 class="card-title" style="font-size:14px">${esc(
        t('meetingGuides.admin.actionTitle'),
      )}</h3></div>
      <p class="text-sm text-muted" style="margin:0 0 10px">${esc(
        t('meetingGuides.admin.actionHelp'),
      )}</p>
      <div class="form-row">
        <div class="form-group" style="flex:1 1 320px">
          <label for="mgm-action-label">${esc(t('meetingGuides.admin.actionLabel'))}</label>
          <input type="text" id="mgm-action-label" maxlength="200" style="width:100%"
            value="${esc(existing?.action_label ?? '')}"
            placeholder="${esc(t('meetingGuides.admin.actionLabelPlaceholder'))}">
        </div>
        <div class="form-group" style="flex:0 0 170px;min-width:150px">
          <label for="mgm-action-delay">${esc(t('meetingGuides.admin.actionDelay'))}</label>
          <input type="number" id="mgm-action-delay" min="-365" max="365" step="1" style="width:100%"
            value="${existing?.action_delay_days ?? 0}">
        </div>
      </div>
      <div class="form-row">
        <div class="form-group" style="flex:1">
          ${checkbox(
            'mgm-action-quote',
            'meetingGuides.admin.actionQuoteMeeting',
            // Coché par défaut, y compris à la création : une action sortie de
            // son contexte doit dire de quelle réunion elle vient.
            existing === null || existing.action_quote_meeting !== false,
          )}
        </div>
      </div>
      <div class="form-row">
        <div class="form-group" style="flex:1">
          <label>${esc(t('meetingGuides.admin.actionPerformers'))}</label>
          ${checkbox('mgm-action-animators', 'meetingGuides.admin.actionAnimators', existing?.action_for_animators === true)}
          ${checkbox('mgm-action-participants', 'meetingGuides.admin.actionParticipants', existing?.action_for_participants === true)}
        </div>
      </div>
    </div>`;
}

function mgShowModal(existing: GuideDefinition | null): void {
  const isNew = existing === null;
  const body = mgModalBody(existing);

  // `openWideModal` et non `openModal` : le bloc « action standard » allonge le
  // formulaire, et la modale étroite laissait ses boutons hors de l'écran.
  openWideModal(
    isNew ? t('meetingGuides.admin.createTitle') : t('meetingGuides.admin.updateTitle'),
    body,
    async () => {
      // Champs verrouillés côté guide système : on renvoie les valeurs
      // d'origine plutôt que celles des champs désactivés (un `disabled` ne
      // garantit rien, et l'API compare pour décider du 409).
      const locked = existing?.is_system === true;
      const name = locked
        ? existing.name
        : (document.getElementById('mgm-name') as HTMLInputElement | null)?.value.trim() || '';
      const description = locked
        ? existing.description
        : (document.getElementById('mgm-desc') as HTMLTextAreaElement | null)?.value.trim() || '';
      const phase = locked
        ? existing.phase
        : ((document.getElementById('mgm-phase') as HTMLSelectElement | null)?.value ?? 'prepare');
      const checked = (id: string): boolean =>
        (document.getElementById(id) as HTMLInputElement | null)?.checked === true;
      const delayRaw = (document.getElementById('mgm-action-delay') as HTMLInputElement | null)
        ?.value;
      if (!name) {
        toast(t('meetingGuides.admin.nameRequired'), 'error');
        return;
      }
      try {
        const payload = {
          name,
          description,
          phase,
          action_label:
            (
              document.getElementById('mgm-action-label') as HTMLInputElement | null
            )?.value.trim() || '',
          action_for_animators: checked('mgm-action-animators'),
          action_for_participants: checked('mgm-action-participants'),
          action_delay_days: Number(delayRaw) || 0,
          action_quote_meeting: checked('mgm-action-quote'),
        };
        if (isNew) await api.createGuideDefinition(payload);
        else await api.updateGuideDefinition(existing.id, payload);
        closeModal();
        toast(t('common.saved'));
        await mgReload();
        mgPaint();
      } catch (err: unknown) {
        mgFail(err);
      }
    },
  );
}

// ── Entrée de page ───────────────────────────────────────────────────────────

pages.adminMeetingGuides = async function () {
  setHeader(
    t('meetingGuides.admin.title'),
    canWriteGuides()
      ? `<button class="btn btn-primary ml-auto" id="btn-new-guide">${esc(
          t('meetingGuides.admin.btnAdd'),
        )}</button>`
      : '',
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    await mgReload();
    mgPaint();
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};
